import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Checkbox,
  Descriptions,
  Empty,
  Input,
  List,
  Modal,
  Space,
  Spin,
  Tag,
  Tooltip,
  Typography,
  message,
} from "antd";
import { ReloadOutlined, RocketOutlined, SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import API_CONFIG from "../config/api.js";
import { getProductionTerminology } from "../GestionProductions/productionTerminology";

const { Paragraph, Text, Title } = Typography;
const API_URL = API_CONFIG.BASE_URL;
const DEFAULT_TERMINOLOGY = getProductionTerminology([]);

const getErrorMessage = (error, fallback) => {
  if (typeof error === "string" && error.trim()) return error;
  return (
    error?.response?.data?.error ||
    error?.response?.data?.message ||
    error?.error ||
    error?.message ||
    fallback
  );
};

const formatInt = (value) =>
  new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
    Number(value || 0)
  );

const formatPrice = (value) =>
  `${new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(
    Number(value || 0)
  )} FCFA`;

const typeLabel = (productType, terminology) =>
  productType === "single"
    ? "Produit fini"
    : terminology.materialSingularTitle;

const typeLabelShort = (productType, terminology) =>
  productType === "single"
    ? "produit fini"
    : productType === "production"
    ? terminology.unitSingular
    : terminology.materialSingular;

// Message de conflit precis et lisible (quel produit, et pourquoi).
const conflictReasonText = (item, terminology) => {
  if (!item) return "";
  if (item.conflict_reason === "deleted_product_exists") {
    return "un produit archive porte deja ce nom dans cet établissement";
  }
  if (item.conflict_reason === "product_type_mismatch") {
    return `existe deja en « ${typeLabelShort(
      item.conflict_existing_type,
      terminology
    )} » (le pack le propose en « ${typeLabelShort(
      item.product_type,
      terminology
    )} »)`;
  }
  if (item.conflict_reason === "unit_mismatch") {
    return `existe deja avec l'unite « ${
      item.conflict_existing_unit || "?"
    } » (le pack propose « ${
      item.conflict_expected_unit || item.unit_short_name || "?"
    } »)`;
  }
  return item.conflict_message || "nom deja utilise differemment";
};

const metricCard = (label, value, color) => (
  <div
    style={{
      padding: "12px 14px",
      borderRadius: 12,
      background: "#fafbfc",
      border: "1px solid #e5e7eb",
      minWidth: 130,
      flex: "1 1 130px",
    }}
  >
    <Text type="secondary" style={{ fontSize: 12, display: "block" }}>
      {label}
    </Text>
    <Text strong style={{ fontSize: 20, color }}>
      {formatInt(value)}
    </Text>
  </div>
);

// Pack de demarrage du CATALOGUE PRODUITS (matieres premieres + produits finis),
// alimente par le pool dynamique inter-entreprises (couche c) + la graine
// curatee. Cases a cocher : l'utilisateur choisit ce qu'il cree (les formes
// rares demarrent decochees). La dedup par nom exact cote serveur garantit
// l'emboitement avec le pack recettes.
const ProductCatalogPackModal = ({
  visible,
  warehouseId,
  warehouseName,
  companyId,
  terminology = DEFAULT_TERMINOLOGY,
  onCancel,
  onApplied,
}) => {
  const [templates, setTemplates] = useState([]);
  const [company, setCompany] = useState(null);
  const [selectedTemplateKey, setSelectedTemplateKey] = useState(null);
  const [preview, setPreview] = useState(null);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [applyLoading, setApplyLoading] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState(() => new Set());
  const [search, setSearch] = useState("");
  const [includeRare, setIncludeRare] = useState(false);

  const selectedTemplate = useMemo(
    () => templates.find((t) => t.key === selectedTemplateKey) || null,
    [templates, selectedTemplateKey]
  );

  const catalog = useMemo(
    () => (Array.isArray(preview?.catalog) ? preview.catalog : []),
    [preview]
  );

  // (Re)initialise la selection a chaque nouveau preview : graine + formes
  // consensuelles (default_selected), uniquement parmi les produits creables.
  useEffect(() => {
    setSelectedKeys(
      new Set(
        catalog
          .filter((item) => item.status === "create" && item.default_selected)
          .map((item) => item.key)
      )
    );
    setSearch("");
    setIncludeRare(false);
  }, [catalog]);

  useEffect(() => {
    if (!visible) {
      setTemplates([]);
      setCompany(null);
      setSelectedTemplateKey(null);
      setPreview(null);
      setTemplatesLoading(false);
      setPreviewLoading(false);
      setApplyLoading(false);
      setSelectedKeys(new Set());
      setSearch("");
      setIncludeRare(false);
      return;
    }

    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const params = {};
        if (companyId) params.company_id = companyId;
        const { data } = await axios.get(
          `${API_URL}/api/production/onboarding/products/templates`,
          { params }
        );
        const list = Array.isArray(data?.templates) ? data.templates : [];
        setCompany(data?.company || null);
        setTemplates(list);
        setSelectedTemplateKey(
          (current) =>
            (current && list.some((t) => t.key === current) && current) ||
            list.find((t) => t.recommended)?.key ||
            list[0]?.key ||
            null
        );
      } catch (error) {
        message.error(
          getErrorMessage(error, "Erreur lors du chargement des packs produits")
        );
        setTemplates([]);
        setSelectedTemplateKey(null);
      } finally {
        setTemplatesLoading(false);
      }
    };

    loadTemplates();
  }, [visible, companyId]);

  useEffect(() => {
    if (!visible || !warehouseId || !selectedTemplateKey) {
      setPreview(null);
      return;
    }

    const loadPreview = async () => {
      setPreviewLoading(true);
      try {
        const params = { warehouse_id: warehouseId };
        if (companyId) params.company_id = companyId;
        const { data } = await axios.get(
          `${API_URL}/api/production/onboarding/products/preview/${selectedTemplateKey}`,
          { params }
        );
        setPreview(data);
      } catch (error) {
        setPreview(null);
        message.error(
          getErrorMessage(error, "Erreur lors de la previsualisation du pack")
        );
      } finally {
        setPreviewLoading(false);
      }
    };

    loadPreview();
  }, [visible, warehouseId, companyId, selectedTemplateKey]);

  const creatableCount = useMemo(
    () => catalog.filter((item) => item.status === "create").length,
    [catalog]
  );
  const reuseCount = useMemo(
    () => catalog.filter((item) => item.status === "reuse").length,
    [catalog]
  );
  const conflictItems = useMemo(
    () => catalog.filter((item) => item.status === "conflict"),
    [catalog]
  );
  const conflictCount = conflictItems.length;
  const selectedCount = selectedKeys.size;

  const filteredCatalog = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return catalog.filter((item) => {
      if (needle && !String(item.name).toLowerCase().includes(needle)) {
        return false;
      }
      // Masque les formes rares (1 etablissement) sauf si demande, mais garde
      // toujours la graine (support null), les deja-presents et les conflits.
      const isRare =
        item.source === "pool" && Number(item.support_count) <= 1;
      if (isRare && !includeRare && item.status === "create") {
        return false;
      }
      return true;
    });
  }, [catalog, search, includeRare]);

  const rareHiddenCount = useMemo(
    () =>
      catalog.filter(
        (item) =>
          item.status === "create" &&
          item.source === "pool" &&
          Number(item.support_count) <= 1
      ).length,
    [catalog]
  );

  const toggleKey = (key, checked) => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const checkVisible = () => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      filteredCatalog.forEach((item) => {
        if (item.status === "create") next.add(item.key);
      });
      return next;
    });
  };

  const uncheckAll = () => setSelectedKeys(new Set());

  const handleApply = async () => {
    if (!warehouseId || selectedCount === 0) {
      message.warning("Selectionnez au moins un produit a initialiser.");
      return;
    }
    setApplyLoading(true);
    try {
      const payload = {
        template_key: selectedTemplateKey,
        warehouse_id: warehouseId,
        selected_product_keys: Array.from(selectedKeys),
      };
      if (companyId) payload.company_id = companyId;
      const { data } = await axios.post(
        `${API_URL}/api/production/onboarding/products/apply`,
        payload
      );
      message.success(
        data?.message || "Catalogue produits initialise avec succes"
      );
      if (onApplied) onApplied(data);
    } catch (error) {
      if (error?.response?.data?.preview) {
        setPreview(error.response.data.preview);
      }
      message.error(
        getErrorMessage(error, "Erreur lors de l'initialisation du pack")
      );
    } finally {
      setApplyLoading(false);
    }
  };

  const renderRow = (item) => {
    const disabled = item.status !== "create";
    const checked = selectedKeys.has(item.key);
    return (
      <List.Item style={{ padding: "8px 4px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 10,
            width: "100%",
          }}
        >
          <Checkbox
            checked={checked && !disabled}
            disabled={disabled}
            onChange={(e) => toggleKey(item.key, e.target.checked)}
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <Space size={6} wrap>
              <Text strong style={{ fontSize: 14 }}>
                {item.name}
              </Text>
              <Tag color={item.product_type === "single" ? "purple" : "blue"}>
                {typeLabel(item.product_type, terminology)}
              </Tag>
              {item.source === "seed" ? (
                <Tag color="gold">Standard</Tag>
              ) : item.support_count ? (
                <Tag color="cyan">{`${formatInt(item.support_count)} etabl.`}</Tag>
              ) : (
                <Tag>Secteur</Tag>
              )}
              {item.status === "reuse" ? (
                <Tag color="green">Deja present</Tag>
              ) : null}
              {item.status === "conflict" ? (
                <Tooltip title={conflictReasonText(item, terminology)}>
                  <Tag color="red">Conflit</Tag>
                </Tooltip>
              ) : null}
            </Space>
            <div style={{ fontSize: 12, color: "#6b7280", marginTop: 2 }}>
              {item.unit_short_name ? `Unite : ${item.unit_short_name}` : "Unite a definir"}
              {item.category_name ? ` · ${item.category_name}` : ""}
              {Number(item.purchase_price) > 0
                ? ` · achat ${formatPrice(item.purchase_price)}`
                : ""}
              {Number(item.sales_price) > 0
                ? ` · vente ${formatPrice(item.sales_price)}`
                : ""}
            </div>
          </div>
        </div>
      </List.Item>
    );
  };

  return (
    <Modal
      open={visible}
      title="Initialiser le catalogue produits"
      onCancel={onCancel}
      onOk={handleApply}
      okText={
        selectedCount > 0
          ? `Creer ${formatInt(selectedCount)} produit(s)`
          : "Appliquer le pack"
      }
      cancelText="Fermer"
      okButtonProps={{
        loading: applyLoading,
        disabled:
          !warehouseId ||
          templatesLoading ||
          previewLoading ||
          !selectedTemplateKey ||
          selectedCount === 0,
      }}
      width={920}
      destroyOnClose
    >
      {!warehouseId ? (
        <Alert
          type="info"
          showIcon
          message="Selectionnez d'abord un établissement pour initialiser le catalogue."
        />
      ) : (
        <Space direction="vertical" size={16} style={{ width: "100%" }}>
          <Alert
            type="info"
            showIcon
            message="Pack de demarrage par secteur"
            description={`Prérempli à partir des produits fréquents de votre secteur. Cochez ce que vous voulez créer : les produits standards et fréquents sont déjà cochés, les produits rares sont masqués (affichables à la demande). Les produits déjà présents sont reconnus et réutilisés (jamais de doublon), ce qui reste compatible avec l'initialisation des ${terminology.configurationPlural}.`}
          />

          <Descriptions
            size="small"
            column={1}
            bordered
            items={[
              {
                key: "company",
                label: "Entreprise",
                children: company?.name || preview?.company?.name || "-",
              },
              {
                key: "profile",
                label: "Profil",
                children:
                  company?.activity_profile_label ||
                  preview?.company?.activity_profile_label ||
                  "Aucun profil renseigne",
              },
              {
                key: "warehouse",
                label: "Établissement",
                children: preview?.warehouse?.name || warehouseName || warehouseId,
              },
              {
                key: "source",
                label: "Source secteur",
                children: `${formatInt(
                  preview?.source_company_count || 0
                )} entreprise(s) de votre secteur`,
              },
            ]}
          />

          {templatesLoading ? (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <Spin />
            </div>
          ) : templates.length === 0 ? (
            <Empty description="Aucun pack disponible pour ce profil d'activite" />
          ) : (
            <div>
              {selectedTemplate?.description ? (
                <Paragraph type="secondary" style={{ marginBottom: 0 }}>
                  {selectedTemplate.description}
                </Paragraph>
              ) : null}
            </div>
          )}

          {previewLoading ? (
            <div style={{ padding: "24px 0", textAlign: "center" }}>
              <Spin />
            </div>
          ) : preview ? (
            <div
              style={{
                padding: 16,
                borderRadius: 14,
                border: "1px solid #c8dced",
                background: "#f7f9fc",
              }}
            >
              <Title level={5} style={{ marginTop: 0 }}>
                {preview.template?.label || selectedTemplate?.label}
              </Title>

              <div
                style={{
                  display: "flex",
                  gap: 10,
                  flexWrap: "wrap",
                  marginBottom: 14,
                }}
              >
                {metricCard("Selectionnes a creer", selectedCount, "#295d86")}
                {metricCard("Disponibles", creatableCount, "#4088b8")}
                {metricCard("Deja presents", reuseCount, "#52c41a")}
                {metricCard("Conflits (ignores)", conflictCount, "#d46b08")}
              </div>

              {conflictCount > 0 ? (
                <Alert
                  type="warning"
                  showIcon
                  style={{ marginBottom: 14 }}
                  message={`${conflictCount} produit(s) non cree(s) : un produit du meme nom existe deja autrement (non selectionnable, sans incidence sur le reste).`}
                  description={
                    <List
                      size="small"
                      split={false}
                      dataSource={conflictItems.slice(0, 12)}
                      renderItem={(item) => (
                        <List.Item style={{ padding: "2px 0", border: "none" }}>
                          <Text>
                            <Text strong>{item.name}</Text>
                            {" — "}
                            {conflictReasonText(item, terminology)}
                          </Text>
                        </List.Item>
                      )}
                      footer={
                        conflictItems.length > 12 ? (
                          <Text type="secondary">{`+ ${
                            conflictItems.length - 12
                          } autre(s)`}</Text>
                        ) : null
                      }
                    />
                  }
                />
              ) : null}

              <div
                style={{
                  display: "flex",
                  gap: 8,
                  flexWrap: "wrap",
                  alignItems: "center",
                  marginBottom: 10,
                }}
              >
                <Input
                  allowClear
                  prefix={<SearchOutlined />}
                  placeholder="Rechercher un produit..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ width: 240 }}
                />
                <Button size="small" onClick={checkVisible}>
                  Cocher les visibles
                </Button>
                <Button size="small" onClick={uncheckAll}>
                  Tout decocher
                </Button>
                {rareHiddenCount > 0 ? (
                  <Checkbox
                    checked={includeRare}
                    onChange={(e) => setIncludeRare(e.target.checked)}
                  >
                    {`Inclure les produits rares (${formatInt(
                      rareHiddenCount
                    )})`}
                  </Checkbox>
                ) : null}
                <Text type="secondary" style={{ marginLeft: "auto" }}>
                  {`${formatInt(selectedCount)} selectionne(s)`}
                </Text>
              </div>

              {filteredCatalog.length === 0 ? (
                <Empty description="Aucun produit ne correspond" />
              ) : (
                <List
                  size="small"
                  dataSource={filteredCatalog}
                  renderItem={renderRow}
                  pagination={
                    filteredCatalog.length > 25
                      ? { pageSize: 25, size: "small", showSizeChanger: false }
                      : false
                  }
                  style={{
                    background: "#fff",
                    borderRadius: 10,
                    border: "1px solid #e5e7eb",
                    padding: "0 10px",
                    maxHeight: 360,
                    overflow: "auto",
                  }}
                />
              )}

              <Space wrap size={[8, 8]} style={{ marginTop: 14 }}>
                <Tag icon={<RocketOutlined />} color="processing">
                  Modifiable apres creation
                </Tag>
                <Tag icon={<ReloadOutlined />}>Ajout sans ecrasement</Tag>
              </Space>
            </div>
          ) : (
            <Empty description="Selectionnez un pack pour afficher la previsualisation" />
          )}
        </Space>
      )}
    </Modal>
  );
};

export default ProductCatalogPackModal;
